import { useState } from "react";
import { InsightProps } from "./insights";
import DataPanel from "@/components/UI/DataPanel";
import InsightList from "./InsightList";
import InstructionsCard from "./InstructionsCard";
import { SortDirection, SORT_DIRECTIONS } from "./insights.constants";
import { sortByDate } from "@/utils/sortByDate";

type InsightsPanelLayoutProps = {
  mineInsights: InsightProps[];
  sharedInsights: InsightProps[];
  isActive: boolean;
  compactMode: boolean;
  onOpenDrawer: (insight: InsightProps) => void;
  onOpenEditModal: (insight: InsightProps) => void;
  onOpenDeleteModal: (
    insight: InsightProps,
    mode?: "insight" | "alert",
  ) => void;
  onOpenDeleteAlertModal: (insight: InsightProps) => void;
  onArchiveInsight: (insight: InsightProps) => void;
  onActivateInsight: (insight: InsightProps) => void;
  onOpenAddAlertModal: (insight: InsightProps) => void;
  onOpenShareModal?: (insight: InsightProps) => void;
};

export default function InsightsPanelLayout({
  mineInsights,
  sharedInsights,
  isActive,
  compactMode,
  onOpenDrawer,
  onOpenEditModal,
  onOpenDeleteModal,
  onOpenDeleteAlertModal,
  onArchiveInsight,
  onActivateInsight,
  onOpenAddAlertModal,
  onOpenShareModal,
}: InsightsPanelLayoutProps) {
  const [mineSortDirection, setMineSortDirection] = useState<SortDirection>(
    SORT_DIRECTIONS.DESC,
  );
  const [sharedSortDirection, setSharedSortDirection] = useState<SortDirection>(
    SORT_DIRECTIONS.DESC,
  );

  const sortedMineInsights = sortByDate(
    mineInsights,
    (insight) => insight.created_at,
    mineSortDirection,
  );
  const sortedSharedInsights = sortByDate(
    sharedInsights,
    (insight) => insight.created_at,
    sharedSortDirection,
  );

  const showEmpty = mineInsights.length === 0 && sharedInsights.length === 0;
  const showInstructionsCard = showEmpty && isActive;

  if (showInstructionsCard) {
    return (
      <div className="flex items-start justify-center px-2 py-4">
        <InstructionsCard />
      </div>
    );
  }

  return (
    <div
      className={`flex ${compactMode ? "flex-col gap-4" : "flex-row gap-3"} py-4`}
    >
      <div className={compactMode ? "w-full" : "w-1/2"}>
        <DataPanel
          title="MINE"
          count={mineInsights.length}
          sortDirection={mineSortDirection}
          onSortChange={setMineSortDirection}
          emptyMessage="No insights have been created"
        >
          <InsightList
            insights={sortedMineInsights}
            isActive={isActive}
            compactMode={compactMode}
            onOpenDrawer={onOpenDrawer}
            onOpenEditModal={onOpenEditModal}
            onOpenDeleteModal={onOpenDeleteModal}
            onOpenDeleteAlertModal={onOpenDeleteAlertModal}
            onArchiveInsight={onArchiveInsight}
            onActivateInsight={onActivateInsight}
            onOpenAddAlertModal={onOpenAddAlertModal}
            onOpenShareModal={onOpenShareModal}
          />
        </DataPanel>
      </div>
      <div className={compactMode ? "w-full" : "w-1/2"}>
        <DataPanel
          title="SHARED"
          count={sharedInsights.length}
          sortDirection={sharedSortDirection}
          onSortChange={setSharedSortDirection}
          emptyMessage="No insights have been shared"
        >
          <InsightList
            insights={sortedSharedInsights}
            isActive={isActive}
            compactMode={compactMode}
            onOpenDrawer={onOpenDrawer}
            onOpenEditModal={onOpenEditModal}
            onOpenDeleteModal={onOpenDeleteModal}
            onOpenDeleteAlertModal={onOpenDeleteAlertModal}
            onArchiveInsight={onArchiveInsight}
            onActivateInsight={onActivateInsight}
            onOpenAddAlertModal={onOpenAddAlertModal}
            onOpenShareModal={onOpenShareModal}
          />
        </DataPanel>
      </div>
    </div>
  );
}
